import { f7 } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import {
  getSubscriptionPlansApi,
  getSubscriptionStatusApi,
} from '@/api/subscription';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { SubscriptionPlan } from '@/types/api/subscription';

// --- LP訴求データ（ハードコード） ---

const painPoints = [
  {
    title: '車の修理代、突然10万円超えることも',
    description: 'エンジン修理、部品代だけで数万円…',
  },
  {
    title: '小傷・へこみ、放置すると査定にひびく',
    description: '小さな傷やへこみ、放置するのも気になる',
  },
  {
    title: '車検・点検のたびに予想外の追加費用がかかる',
    description: '車検・点検のたびに追加費用が発生する',
  },
];

const serviceContents = [
  {
    title: '修理優待',
    description: '提携工場での対象整備・対象修理に会員価格を適用',
    included: [
      'エンジン・駆動系・電装系・ブレーキ系・サスペンション等',
      '事前見積承認制（工賃優待中心）',
      '部品: 中古→リビルト→社外新品→純正の優先順',
    ],
    excluded: [
      '消耗品（タイヤ・バッテリー・ブレーキパッド等）',
      '事故の鈑金全面修理・社外品・競技用部品',
    ],
  },
  {
    title: '軽補修優待',
    description: '提携工場での対象軽補修メニューに会員価格を適用',
    included: [
      '小傷・ガラスリペア・軽微なへこみ・ホイール補修',
      '事前見積承認制',
    ],
    excluded: ['板金全面・大事故修理・部品交換を伴う大規模修理'],
  },
];

const targetCategories = [
  'エンジン系',
  '駆動系',
  '電装系',
  'ブレーキ系',
  'ガラスリペア',
  '軽微なへこみ',
];

const repairExamples = [
  {
    title: 'スライドドアが動かなくなった',
    car: 'HONDA ステップワゴン（平成28年式）',
    mileage: '走行 72,000km',
    detail: '修理内容: スライドドアモーター交換',
    originalPrice: 140000,
  },
  {
    title: 'エアコンが効かなくなった',
    car: 'TOYOTA ヴォクシー（平成30年式）',
    mileage: '走行 65,000km',
    detail: '修理内容: エアコンコンプレッサー交換',
    originalPrice: 140000,
  },
  {
    title: 'パワーウィンドウが動かない',
    car: 'NISSAN セレナ（平成29年式）',
    mileage: '走行 80,000km',
    detail: '修理内容: パワーウィンドウモーター交換',
    originalPrice: 140000,
  },
];

const trustStats = [
  { label: '顧客満足度', value: '98%', note: '会員アンケートより' },
  { label: '優待利用実績', value: '10万件以上', note: '豊富な利用実績' },
  { label: 'ロードサービス', value: '24時間対応', note: '365日いつでも出動' },
];

const customerVoices = [
  {
    title: '12年落ちの車でも会員になれた',
    body: '12年落ちの軽。エンジン警告灯が点いてディーラーへ。修理見積は約¥8.8万。他社の保証サービスは年式で断られたがmaikuruは加入でき、会員価格で対応してもらえた。',
    profile: '40代・男性・軽自動車 12年目',
  },
  {
    title: '進捗がアプリで全部わかった',
    body: 'ATトランスミッション交換。通常¥15万超の修理が、会員価格で対応。アプリで見積確認→承認まで完結。工場への電話は一切不要だった。',
    profile: '30代・女性・コンパクトカー 8年目',
  },
];

const enrollmentSteps = [
  { step: '01', title: 'アプリをダウンロード' },
  { step: '02', title: '会員登録・車検証登録' },
  { step: '03', title: 'メンバーシップに加入' },
];

const faqItems = [
  {
    question: 'どの車でも加入できますか？',
    answer:
      '国産車も輸入車も、年式・走行距離を問わずご加入いただけます。保証適用には条件がございますので、詳しくは利用規約をご確認ください。',
  },
  {
    question: '加入したその日から使えますか？',
    answer: 'はい、ご加入いただいた当日からサービスをご利用いただけます。',
  },
  {
    question: '費用はどのくらいかかりますか？',
    answer: null, // 金額はplanから動的に生成
  },
  {
    question: '加入できない車や対象外の修理はありますか？',
    answer:
      '消耗品（タイヤ・バッテリー・ブレーキパッド等）や事故の鈑金全面修理・社外品・競技用部品は対象外です。詳しくは利用規約をご確認ください。',
  },
  {
    question: '解約の条件はありますか？',
    answer: 'いつでも解約が可能です。解約後は翌月からサービスが停止されます。',
  },
  {
    question: '加入後に返金はできますか？',
    answer:
      '月額料金の日割り返金は行っておりません。解約月の末日までサービスをご利用いただけます。',
  },
  {
    question: '支払いが失敗した場合はどうなりますか？',
    answer:
      '月内に3〜4回、引き落としを再試行します。それでも失敗した場合、2ヶ月後にサービスが停止されます。お申し込み時にお支払い方法をご確認ください。',
  },
];

// --- セクションヘッダー（洗車ページパターン流用） ---

const SectionHead: FC<{
  enTitle: string;
  jaTitle: string;
  accentColor?: string;
}> = ({ enTitle, jaTitle, accentColor = '#1E55E6' }) => (
  <div
    style={{
      textAlign: 'center',
      paddingBottom: '14px',
      marginBottom: '24px',
      position: 'relative',
    }}
  >
    <div
      style={{
        fontSize: '28px',
        fontWeight: 700,
        color: accentColor,
        lineHeight: 1.5,
      }}
    >
      {enTitle}
    </div>
    <div style={{ fontWeight: 700, lineHeight: 1.5, color: '#222' }}>
      {jaTitle}
    </div>
    <div
      style={{
        position: 'absolute',
        width: '26px',
        height: '2px',
        backgroundColor: accentColor,
        bottom: 0,
        left: 'calc(50% - 13px)',
      }}
    />
  </div>
);

// --- コンポーネント ---

export const SubscriptionDetailPage: FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const init = async () => {
      // 契約済みの場合はマイページにリダイレクト
      try {
        const authInfo = store.state.authInfo as any;
        if (authInfo?.m_customer_id) {
          const statusRes = await getSubscriptionStatusApi(
            authInfo.m_customer_id,
          );
          if (statusRes.data.success && statusRes.data.data?.has_subscription) {
            f7.view.main.router.navigate(paths.subscriptionMyPage, {
              reloadCurrent: true,
            });
            return;
          }
        }
      } catch {
        // チェック失敗は無視してプラン表示を続行
      }

      try {
        const res = await getSubscriptionPlansApi();
        if (res.data.success && res.data.data) {
          setPlans(res.data.data.plans);
        }
      } catch (e) {
        console.error('プラン取得エラー', e);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const handleClickStart = () => {
    f7.view.main.router.navigate(paths.subscriptionConfirm);
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  if (isLoading) {
    return (
      <PageTemplate
        pageName="subscription-detail"
        title="マイくるプラス"
        showNavBack
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          読み込み中...
        </div>
      </PageTemplate>
    );
  }

  const plan = plans[0];

  if (!plan) {
    return (
      <PageTemplate
        pageName="subscription-detail"
        title="マイくるプラス"
        showNavBack
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          現在利用可能なプランはありません
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      pageName="subscription-detail"
      title="マイくるプラス"
      showNavBack
    >
      <style>{`
        [name="subscription-detail"] .page-content > div {
          --background-color: #F5F5F7 !important;
          background-color: #F5F5F7 !important;
        }
      `}</style>
      <div style={{ margin: '0 -16px', padding: '0 16px' }}>
        {/* ===== S1: ファーストビュー ===== */}
        <div style={{ padding: '32px 0 24px', textAlign: 'center' }}>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              lineHeight: 1.4,
              marginBottom: '12px',
            }}
          >
            修理代に、もう驚かない。
          </h1>
          <p
            style={{
              fontSize: '14px',
              color: '#555',
              lineHeight: 1.7,
              marginBottom: '20px',
            }}
          >
            REVISE提携工場での修理が、
            <br />
            月額¥{plan.monthly_fee.toLocaleString()}でいつでも優待価格に。
            <br />
            全車種OK・加入当日から・いつでも解約。
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px',
            }}
          >
            {['全車種OK', '加入当日から', 'アプリ完結'].map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'inline-block',
                  padding: '4px 14px',
                  fontSize: '12px',
                  color: '#333',
                  backgroundColor: '#fff',
                  borderRadius: '20px',
                  border: '1px solid #ddd',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
          <button
            onClick={handleClickStart}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#fff',
              backgroundColor: '#1a1a1a',
              border: 'none',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            まずは申し込む
          </button>
        </div>

        {/* ===== S2: 課題提起 ===== */}
        <div style={{ paddingBottom: '24px' }}>
          <SectionHead enTitle="PROBLEM" jaTitle="こんな経験、ありませんか？" />
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {painPoints.map((pain, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      minHeight: '40px',
                      backgroundColor: '#1a1a1a',
                      borderRadius: '2px',
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '6px',
                      }}
                    >
                      {pain.title}
                    </div>
                    <div style={{ fontSize: '13px', color: '#888' }}>
                      {pain.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== S3: サービス内容 ===== */}
        <div style={{ paddingBottom: '24px' }}>
          <SectionHead enTitle="SERVICE" jaTitle="サービス内容" />
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {serviceContents.map((service, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {service.title}
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '16px',
                  }}
                >
                  {service.description}
                </div>
                {service.included.map((item, j) => (
                  <div
                    key={`inc-${j}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      fontSize: '13px',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#1a1a1a', flexShrink: 0 }}>●</span>
                    <span>{item}</span>
                  </div>
                ))}
                {service.excluded.map((item, j) => (
                  <div
                    key={`exc-${j}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      fontSize: '13px',
                      marginBottom: '6px',
                      color: '#C62828',
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>×</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* 価格 */}
          <div
            style={{
              marginTop: '16px',
              padding: '24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '8px',
              }}
            >
              上記のサービスが・・・
            </div>
            <div style={{ marginBottom: '4px' }}>
              <span
                style={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                }}
              >
                ¥{plan.monthly_fee.toLocaleString()}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              月額（税込）・1日あたり約
              {Math.round(plan.monthly_fee / 30)}円
            </div>
          </div>
        </div>

        {/* ===== S4: 対象カテゴリ ===== */}
        <div
          style={{
            margin: '0 -16px',
            padding: '24px 16px',
            backgroundColor: '#f6f5f2',
          }}
        >
          <SectionHead enTitle="COVERAGE" jaTitle="サービスの対象カテゴリ" />
          <p
            style={{
              fontSize: '13px',
              color: '#666',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            下記の「対象カテゴリ内」の修理・補修に会員価格を適用できます
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '24px',
            }}
          >
            {targetCategories.map((cat) => (
              <div
                key={cat}
                style={{
                  padding: '16px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                {cat}
              </div>
            ))}
          </div>

          {/* 修理事例 */}
          <SectionHead enTitle="CASE" jaTitle="修理事例" />
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {repairExamples.map((ex, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {ex.title}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    marginBottom: '4px',
                  }}
                >
                  {ex.car}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    marginBottom: '12px',
                  }}
                >
                  {ex.mileage}
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#555',
                    marginBottom: '12px',
                  }}
                >
                  {ex.detail}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '12px',
                    backgroundColor: '#FFF8E1',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                      一般修理費用
                    </div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#C62828',
                        textDecoration: 'line-through',
                      }}
                    >
                      ¥{ex.originalPrice.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ fontSize: '20px', color: '#888' }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                      会員価格
                    </div>
                    <div
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1E55E6',
                      }}
                    >
                      ¥0
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== S5: 実績・信頼 ===== */}
        <div
          style={{
            margin: '0 -16px',
            padding: '32px 16px',
            backgroundColor: '#f6f5f2',
            color: '#333',
          }}
        >
          <SectionHead
            enTitle="VOICE"
            jaTitle="数字で見る安心とお客様の声"
            accentColor="#1E55E6"
          />
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
            }}
          >
            {trustStats.map((stat, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: '16px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    color: '#888',
                    marginBottom: '4px',
                  }}
                >
                  {stat.label}
                </div>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  {stat.value}
                </div>
                <div style={{ fontSize: '10px', color: '#888' }}>
                  {stat.note}
                </div>
              </div>
            ))}
          </div>

          {/* お客様の声 */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {customerVoices.map((voc, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  border: '1px solid #ccc',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      minHeight: '40px',
                      backgroundColor: '#1E55E6',
                      borderRadius: '2px',
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '8px',
                      }}
                    >
                      {voc.title}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: 1.7,
                        marginBottom: '12px',
                      }}
                    >
                      「{voc.body}」
                    </div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      {voc.profile}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== FAQ ===== */}
        <div
          style={{
            margin: '0 -16px',
            padding: '24px 16px',
            backgroundColor: '#f6f5f2',
          }}
        >
          <SectionHead enTitle="FAQ" jaTitle="よくある質問" />
          {faqItems.map((faq, i) => (
            <div
              key={i}
              style={{
                borderBottom: '1px solid #e0e0e0',
                overflow: 'hidden',
              }}
            >
              <div
                onClick={() => toggleFaq(i)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  minHeight: '44px',
                }}
              >
                <span>Q. {faq.question}</span>
                <span
                  style={{
                    transform:
                      openFaqIndex === i ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s',
                    fontSize: '12px',
                    flexShrink: 0,
                    marginLeft: '8px',
                  }}
                >
                  ▼
                </span>
              </div>
              {openFaqIndex === i && (
                <div
                  style={{
                    padding: '0 0 14px',
                    fontSize: '13px',
                    color: '#666',
                    lineHeight: 1.7,
                  }}
                >
                  A.{' '}
                  {faq.answer ??
                    `月額¥${plan.monthly_fee.toLocaleString()}（税込）です。修理を利用しない月も定額のお支払いとなります。`}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* フッター分の余白 */}
        <div style={{ height: '80px' }} />
      </div>

      {/* 固定フッターCTA */}
      <div
        style={{
          position: 'fixed',
          bottom: '90px',
          left: 0,
          right: 0,
          padding: '12px 16px',
          backgroundColor: '#fff',
          borderTop: '1px solid #e0e0e0',
          zIndex: 100,
        }}
      >
        <button
          onClick={handleClickStart}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '15px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: '#1E55E6',
            border: 'none',
            borderRadius: '28px',
            cursor: 'pointer',
          }}
        >
          {plan.plan_name}をはじめる
        </button>
      </div>
    </PageTemplate>
  );
};
